const POSTHOG_HOST = (process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com').replace(/\/$/, '');
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
const POSTHOG_ENABLED = process.env.REACT_APP_POSTHOG_ENABLED === 'true';
const DISTINCT_ID_KEY = 'promotify_posthog_distinct_id';
const USER_DISTINCT_ID_KEY = 'promotify_posthog_user_distinct_id';

type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

const compact = (properties: AnalyticsProperties = {}) =>
    Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined && value !== null));

export const getAnonymousDistinctId = () => {
    let id = window.localStorage.getItem(DISTINCT_ID_KEY);
    if (!id) {
        id = `anon_${crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(16).slice(2)}`}`;
        window.localStorage.setItem(DISTINCT_ID_KEY, id);
    }
    return id;
};

const getDistinctId = () => {
    return window.localStorage.getItem(USER_DISTINCT_ID_KEY) || getAnonymousDistinctId();
};

export const getPageProperties = (): AnalyticsProperties => {
    const params = new URLSearchParams(window.location.search);

    return compact({
        page_url: window.location.href,
        page_path: window.location.pathname,
        referrer: document.referrer || undefined,
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        utm_content: params.get('utm_content'),
        utm_term: params.get('utm_term'),
        invited_from: params.get('invitedFrom'),
        environment: process.env.REACT_APP_ENV || process.env.NODE_ENV
    });
};

export const trackEvent = (event: string, properties: AnalyticsProperties = {}) => {
    if (!POSTHOG_ENABLED || !POSTHOG_KEY || typeof window === 'undefined') return;

    const payload = JSON.stringify({
        api_key: POSTHOG_KEY,
        event,
        distinct_id: getDistinctId(),
        properties: compact({
            ...getPageProperties(),
            ...properties
        })
    });

    const url = `${POSTHOG_HOST}/capture/`;

    try {
        if (navigator.sendBeacon) {
            const sent = navigator.sendBeacon(url, new Blob([payload], {type: 'application/json'}));
            if (sent) return;
        }

        fetch(url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: payload,
            keepalive: true
        }).catch(() => undefined);
    } catch {
        // Analytics must never block product behavior.
    }
};

export const identifyUser = (userId: string, properties: AnalyticsProperties = {}) => {
    if (!POSTHOG_ENABLED || !POSTHOG_KEY || typeof window === 'undefined' || !userId) return;

    const anonymousId = getAnonymousDistinctId();
    window.localStorage.setItem(USER_DISTINCT_ID_KEY, userId);

    const payload = JSON.stringify({
        api_key: POSTHOG_KEY,
        event: '$identify',
        distinct_id: userId,
        properties: compact({
            ...getPageProperties(),
            ...properties,
            distinct_id: userId,
            $anon_distinct_id: anonymousId
        })
    });

    try {
        fetch(`${POSTHOG_HOST}/capture/`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: payload,
            keepalive: true
        }).catch(() => undefined);
    } catch {
        // Analytics must never block product behavior.
    }
};

export const resetAnalyticsUser = () => {
    window.localStorage.removeItem(USER_DISTINCT_ID_KEY);
};
